import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LEAD_STATUSES, LEAD_STATUS_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { Lead } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import { CompanyAutocomplete } from './CompanyAutocomplete';
import styles from './Leads.module.css';

type Props = {
  projectId: string;
  onClose: () => void;
  onCreated?: (lead: Lead) => void;
};

export function CreateLeadModal({ projectId, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [status, setStatus] = useState<string>('new');
  const [notes, setNotes] = useState('');
  const [outreachReason, setOutreachReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const custom: Record<string, string> = {};
      if (industry.trim()) custom.industry = industry.trim();
      if (companySize.trim()) custom.company_size = companySize.trim();
      return apiFetch<{ lead: Lead }>('/api/leads', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          account_id: accountId,
          contact_name: contactName.trim() || null,
          email: email.trim() || null,
          company: company.trim() || null,
          phone: phone.trim() || null,
          website: website.trim() || null,
          job_title: jobTitle.trim() || null,
          linkedin_url: linkedinUrl.trim() || null,
          status,
          notes: notes.trim() || null,
          outreach_reason: outreachReason.trim() || null,
          custom_fields: Object.keys(custom).length ? custom : undefined,
        }),
      });
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['accounts-autocomplete'] });
      onCreated?.(data.lead);
      onClose();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!email.trim() && !contactName.trim()) {
      setFormError('Vul minimaal een e-mailadres of contactnaam in.');
      return;
    }
    create.mutate();
  }

  return (
    <div className={styles.modalBackdrop} role="presentation" onClick={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        role="dialog"
        aria-labelledby="create-lead-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.modalHeader}>
          <h2 id="create-lead-title" className={styles.modalTitle}>
            Nieuwe lead
          </h2>
          <Button type="button" variant="ghost" onClick={onClose}>
            Sluiten
          </Button>
        </header>
        <form className={styles.modalForm} onSubmit={onSubmit}>
          <h3 className={styles.formSectionTitle}>Contact</h3>
          <div className={styles.modalGrid}>
            <Field label="Contactnaam">
              <TextInput value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </Field>
            <Field label="E-mail">
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
            </Field>
            <Field label="Telefoon">
              <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field label="Functie">
              <TextInput value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
            </Field>
            <Field label="LinkedIn">
              <TextInput value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} />
            </Field>
          </div>

          <h3 className={styles.formSectionTitle}>Bedrijf</h3>
          <div className={styles.modalGrid}>
            <Field label="Bedrijfsnaam" hint="Autocomplete uit accounts en bestaande leads">
              <CompanyAutocomplete
                projectId={projectId}
                value={company}
                onValueChange={(name) => {
                  setCompany(name);
                  setAccountId(null);
                }}
                onSelect={(sel) => {
                  setCompany(sel.name);
                  setAccountId(sel.account_id);
                  if (sel.website) setWebsite(sel.website);
                  if (sel.industry) setIndustry(sel.industry);
                  if (sel.company_size) setCompanySize(sel.company_size);
                }}
              />
            </Field>
            <Field label="Website">
              <TextInput value={website} onChange={(e) => setWebsite(e.target.value)} />
            </Field>
            <Field label="Branche">
              <TextInput value={industry} onChange={(e) => setIndustry(e.target.value)} />
            </Field>
            <Field label="Bedrijfsgrootte">
              <TextInput
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                placeholder="bijv. 1–10, MKB, enterprise"
              />
            </Field>
          </div>

          <h3 className={styles.formSectionTitle}>CRM</h3>
          <div className={styles.modalGrid}>
            <Field label="Status">
              <SelectInput value={status} onChange={(e) => setStatus(e.target.value)}>
                {LEAD_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {LEAD_STATUS_LABELS[s]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Outreach-reden">
              <TextInput
                value={outreachReason}
                onChange={(e) => setOutreachReason(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Notities">
            <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </Field>
          {formError ? (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          ) : null}
          {create.isError ? (
            <p className={styles.formError} role="alert">
              {(create.error as Error).message}
            </p>
          ) : null}
          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Opslaan…' : 'Lead aanmaken'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
